import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];
const LIMIT = 15;

function fmtMoney(n) { return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function Payroll({ setTitle }) {
  const now = new Date();
  const [year, setYear]     = useState(now.getFullYear());
  const [month, setMonth]   = useState(now.getMonth() + 1);
  const [page, setPage]     = useState(1);
  const [loading, setLoading] = useState(false);
  const [items, setItems]   = useState([]);
  const [total, setTotal]   = useState(0);
  const [summary, setSummary] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [modal, setModal]   = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [form, setForm]     = useState({ employee_id: '', basic_salary: '', deductions: '0', bonus: '0', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { setTitle('Payroll'); }, [setTitle]);
  useEffect(() => {
    api.get('/employees?status=active&limit=200').then(r => setEmployees(r.data?.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [payRes, sumRes] = await Promise.all([
        api.get(`/payroll?month=${month}&year=${year}&page=${page}&limit=${LIMIT}`),
        api.get(`/payroll/summary/${year}/${month}`),
      ]);
      setItems(payRes.data?.data || []); setTotal(payRes.data?.total || 0);
      setSummary(sumRes.data || null);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [month, year, page]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    if (!window.confirm(`Generate payroll for ${MONTHS[month - 1]} ${year}? This will create payslips for all active employees.`)) return;
    setGenerating(true);
    try {
      const { data } = await api.post('/payroll/generate', { month, year });
      alert(`Generated ${data.count || 0} payslip(s). ${data.skipped || 0} skipped (already exists).`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error generating payroll');
    } finally { setGenerating(false); }
  };

  const openCreate = () => {
    setForm({ employee_id: '', basic_salary: '', deductions: '0', bonus: '0', notes: '' });
    setModal(true);
  };

  const openView = async (id) => {
    try { const { data } = await api.get(`/payroll/${id}`); setViewModal(data); }
    catch { alert('Could not load payslip'); }
  };

  const handleSave = async () => {
    if (!form.employee_id) return alert('Employee is required');
    if (!form.basic_salary) return alert('Basic salary is required');
    setSaving(true);
    try {
      await api.post('/payroll', { ...form, month, year });
      setModal(false); load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error creating payslip');
    } finally { setSaving(false); }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await api.put(`/payroll/${id}`, { status });
      load();
      if (viewModal?.id === id) { const { data } = await api.get(`/payroll/${id}`); setViewModal(data); }
    } catch (err) { alert(err.response?.data?.error || 'Error updating'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payslip?')) return;
    try { await api.delete(`/payroll/${id}`); load(); if (viewModal?.id === id) setViewModal(null); }
    catch (err) { alert(err.response?.data?.error || 'Error deleting'); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const totalPages = Math.ceil(total / LIMIT);
  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Payroll</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={openCreate}>+ Manual Entry</button>
          <button className="btn btn-primary" onClick={handleGenerate} disabled={generating}>
            {generating ? 'Generating…' : 'Generate Payroll'}
          </button>
        </div>
      </div>

      {/* Period selector */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="toolbar">
          <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 0 }}>
            <label style={{ whiteSpace: 'nowrap', marginBottom: 0 }}>Period:</label>
            <select className="form-control" style={{ width: 140 }} value={month} onChange={e => { setMonth(Number(e.target.value)); setPage(1); }}>
              {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
            <select className="form-control" style={{ width: 90 }} value={year} onChange={e => { setYear(Number(e.target.value)); setPage(1); }}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{total} payslips</span>
        </div>

        {/* Summary bar */}
        {summary && (
          <div className="stats-grid" style={{ padding: '0 20px 16px', margin: 0 }}>
            {[
              { label: 'Total Basic', value: fmtMoney(summary.total_basic) },
              { label: 'Total Deductions', value: fmtMoney(summary.total_deductions) },
              { label: 'Total Bonuses', value: fmtMoney(summary.total_bonuses) },
              { label: 'Net Payable', value: fmtMoney(summary.total_net) },
              { label: 'Paid', value: summary.paid_count || 0 },
            ].map(s => (
              <div className="stat-card" key={s.label}>
                <div className="label">{s.label}</div>
                <div className="value" style={{ fontSize: 18 }}>{s.value}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <div className="loading">Loading…</div> : (
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th className="text-right">Basic</th>
                  <th className="text-right">Deductions</th>
                  <th className="text-right">Bonus</th>
                  <th className="text-right">Net</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={7} className="empty">No payslips for {MONTHS[month - 1]} {year}. Click "Generate Payroll" to create them.</td></tr>
                ) : items.map(item => (
                  <tr key={item.id}>
                    <td><strong>{item.employee_name}</strong></td>
                    <td className="text-right">{fmtMoney(item.basic_salary)}</td>
                    <td className="text-right" style={{ color: '#dc2626' }}>{fmtMoney(item.deductions)}</td>
                    <td className="text-right" style={{ color: '#15803d' }}>{fmtMoney(item.bonus)}</td>
                    <td className="text-right"><strong>{fmtMoney(item.net_salary)}</strong></td>
                    <td><span className={`badge badge-${item.status}`}>{item.status}</span></td>
                    <td>
                      <div className="actions">
                        <button className="btn-icon" title="View" onClick={() => openView(item.id)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>
                        </button>
                        {item.status === 'pending' && (
                          <button className="btn-icon success" title="Mark Paid" onClick={() => handleUpdateStatus(item.id, 'paid')}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                          </button>
                        )}
                        <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(item.id)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <span>Showing {Math.min((page - 1) * LIMIT + 1, total)}–{Math.min(page * LIMIT, total)} of {total}</span>
            <div className="pagination-btns">
              <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {/* Manual entry modal */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>Manual Payslip — {MONTHS[month - 1]} {year}</h2>
              <button className="modal-close" onClick={() => setModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full">
                  <label>Employee *</label>
                  <select className="form-control" value={form.employee_id} onChange={e => {
                    const emp = employees.find(x => String(x.id) === e.target.value);
                    f('employee_id', e.target.value);
                    if (emp) f('basic_salary', emp.salary || '');
                  }}>
                    <option value="">Select employee…</option>
                    {employees.map(e => <option key={e.id} value={e.id}>{e.name} — {e.department || 'N/A'}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Basic Salary *</label>
                  <input type="number" className="form-control" value={form.basic_salary} onChange={e => f('basic_salary', e.target.value)} placeholder="0.00" step="0.01" min="0" />
                </div>
                <div className="form-group">
                  <label>Deductions</label>
                  <input type="number" className="form-control" value={form.deductions} onChange={e => f('deductions', e.target.value)} placeholder="0.00" step="0.01" min="0" />
                </div>
                <div className="form-group">
                  <label>Bonus</label>
                  <input type="number" className="form-control" value={form.bonus} onChange={e => f('bonus', e.target.value)} placeholder="0.00" step="0.01" min="0" />
                </div>
                <div className="form-group">
                  <label>Net (calculated)</label>
                  <div style={{ padding: '8px 0', fontWeight: 700, fontSize: 16 }}>
                    {fmtMoney((parseFloat(form.basic_salary || 0) - parseFloat(form.deductions || 0) + parseFloat(form.bonus || 0)))}
                  </div>
                </div>
                <div className="form-group full">
                  <label>Notes</label>
                  <textarea className="form-control" value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Optional notes…" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Create Payslip'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View payslip modal */}
      {viewModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setViewModal(null)}>
          <div className="modal">
            <div className="modal-header">
              <h2>Payslip — {viewModal.employee_name}</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost btn-sm no-print" onClick={() => window.print()}>Print</button>
                <button className="modal-close" onClick={() => setViewModal(null)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Employee</label><div><strong>{viewModal.employee_name}</strong></div>
                </div>
                <div className="form-group">
                  <label>Period</label><div>{MONTHS[viewModal.month - 1]} {viewModal.year}</div>
                </div>
                <div className="form-group">
                  <label>Status</label><div><span className={`badge badge-${viewModal.status}`}>{viewModal.status}</span></div>
                </div>
                <div className="form-group">
                  <label>Basic Salary</label><div>{fmtMoney(viewModal.basic_salary)}</div>
                </div>
                <div className="form-group">
                  <label>Deductions</label><div style={{ color: '#dc2626' }}>- {fmtMoney(viewModal.deductions)}</div>
                </div>
                <div className="form-group">
                  <label>Bonus</label><div style={{ color: '#15803d' }}>+ {fmtMoney(viewModal.bonus)}</div>
                </div>
                <div className="form-group full">
                  <hr className="section-divider" />
                  <label>Net Salary</label>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtMoney(viewModal.net_salary)}</div>
                </div>
                {viewModal.notes && (
                  <div className="form-group full">
                    <label>Notes</label><div>{viewModal.notes}</div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              {viewModal.status === 'pending' && (
                <button className="btn btn-success" onClick={() => handleUpdateStatus(viewModal.id, 'paid')}>Mark as Paid</button>
              )}
              <button className="btn btn-ghost" onClick={() => setViewModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
